// Third-party
import React, { Component } from 'react';
import * as Yup from 'yup';
import { withFormik, FormikProps, Form } from 'formik';

// Components
import { FormInput } from '../../../components/forms';
import { PrimaryButton } from '../../../components/buttons';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import notification from '../../../../utils/notification';
import { PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import Enterprise from '@/types/enterprise';
import User from '@/types/user';
import EnterpriseInfos from '@/types/enterprise-infos';

// TODO: @deprecate when we deprecate Legacy
import LegacyEnterpriseDetails from './LegacyEnterpriseDetails';
import useThemeVersion from '@/hooks/useThemeVersion';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';

interface ParentProps {
  adminDetails: EnterpriseAdminDetails;
  user: User;
  onSave: (infos: EnterpriseInfos) => Promise<Enterprise>;
}

type Props = ParentProps & FormikProps<EnterpriseInfos>;

class EnterpriseDetails extends Component<Props> {
  canSubmit = (readOnly: boolean) => {
    const { values, initialValues, isSubmitting, errors } = this.props;
    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;
    return !readOnly && !hasErrors && isDirty && !isSubmitting;
  };

  render() {
    const { user, adminDetails } = this.props;
    const readOnly = !userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN);
    if (!adminDetails) {
      return null;
    }

    const { usedLicenses, totalLicenses, usedLimitedLicenses, totalLimitedLicenses, nbAdmins } = adminDetails;

    return (
      <div className="enterprise-details">
        <h3>
          <FormattedMessage id="Admin.Enterprise.CompanyInfo" />
        </h3>

        <Form className="enterprise-details-form d-flex align-items-end">
          <FormInput className="form-input-v2" disabled={readOnly} label={formatMessage('Admin.Enterprise.Fields.Name')} name="name" required />

          <PrimaryButton className="save-button primary-v2" disabled={!this.canSubmit(readOnly)} type="submit">
            <FormattedMessage id="Common.Save" />
          </PrimaryButton>
        </Form>

        {totalLicenses && (
          <span className="active-licenses-text">
            <FormattedMessage id="Admin.Enterprise.ActiveLicenses" />
            <span className="active-licenses-number"> {`${usedLicenses}/${totalLicenses}`}</span>
            &nbsp;{`(+${nbAdmins} ${formatMessage('Admin.Enterprise.Administrators')})`}
          </span>
        )}

        {!!totalLimitedLicenses && (
          <span className="active-licenses-text">
            <FormattedMessage id="Admin.Enterprise.LimitedLicenses" />
            <span className="active-licenses-number"> {`${usedLimitedLicenses}/${totalLimitedLicenses}`}</span>
          </span>
        )}
      </div>
    );
  }
}

const EnterpriseDetailsComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseDetails {...props} />;
  }
  return <LegacyEnterpriseDetails {...props} />;
};

const EnterpriseSchema = Yup.object().shape({
  name: Yup.string().required(),
});

const formikWrapper = withFormik<Props, EnterpriseInfos>({
  validationSchema: EnterpriseSchema,
  mapPropsToValues: props => ({ ...props.enterprise }),
  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    props
      .onSave(values)
      .then((enterprise: Enterprise) => {
        notification.success(formatMessage('Admin.Enterprise.SaveInfoSuccess'));
        resetForm(values);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(EnterpriseDetailsComponent) as any;
